// Hero Illustration Component - AI Learning Journey Visualization
import React from 'react';

interface HeroIllustrationProps {
  isDark: boolean;
}

export const HeroIllustration: React.FC<HeroIllustrationProps> = ({ isDark }) => {
  return (
    <div className="relative w-full h-96 flex items-center justify-center">
      <svg
        viewBox="0 0 400 300"
        className="w-full max-w-md h-auto"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Define gradients and patterns */}
        <defs>
          <linearGradient id="heroGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EF4444" stopOpacity="1" />
            <stop offset="50%" stopColor="#F97316" stopOpacity="1" />
            <stop offset="100%" stopColor="#F43F5E" stopOpacity="1" />
          </linearGradient>

          <linearGradient id="heroGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366F1" stopOpacity="1" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="1" />
          </linearGradient>

          <linearGradient id="heroGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="1" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="1" />
          </linearGradient>

          <filter id="heroGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="heroShadow">
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity="0.3" />
          </filter>

          <style>{`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            @keyframes pulse-glow {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            @keyframes rotate-slow {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .hero-float { animation: float 3s ease-in-out infinite; }
            .hero-pulse { animation: pulse-glow 2s ease-in-out infinite; }
            .hero-rotate { animation: rotate-slow 20s linear infinite; }
          `}</style>
        </defs>

        {/* Background orbs */}
        <circle
          cx="80"
          cy="60"
          r="40"
          fill={isDark ? '#EF4444' : '#FCA5A5'}
          opacity="0.1"
          className="hero-pulse"
        />
        <circle
          cx="320"
          cy="240"
          r="50"
          fill={isDark ? '#3B82F6' : '#BFDBFE'}
          opacity="0.1"
          className="hero-pulse"
          style={{ animationDelay: '0.5s' }}
        />

        {/* Central learning node - Brain/Lightbulb concept */}
        <g className="hero-float">
          {/* Brain outline with gradient */}
          <circle
            cx="200"
            cy="120"
            r="45"
            fill="url(#heroGradient1)"
            filter="url(#heroShadow)"
          />

          {/* Inner glow */}
          <circle
            cx="200"
            cy="120"
            r="42"
            fill="none"
            stroke="url(#heroGradient1)"
            strokeWidth="2"
            opacity="0.5"
          />

          {/* Brain wave pattern inside */}
          <path
            d="M 165 120 Q 175 110 185 120 T 215 120 T 235 120"
            stroke="white"
            strokeWidth="2"
            fill="none"
            opacity="0.8"
            strokeLinecap="round"
          />
          <path
            d="M 165 135 Q 175 125 185 135 T 215 135 T 235 135"
            stroke="white"
            strokeWidth="2"
            fill="none"
            opacity="0.6"
            strokeLinecap="round"
          />

          {/* Center spark */}
          <circle cx="200" cy="120" r="8" fill="white" opacity="0.9" />
        </g>

        {/* Learning paths - Ascending progression */}
        <g opacity="0.8">
          {/* Left path - blue */}
          <g>
            <line x1="120" y1="200" x2="140" y2="160" stroke="url(#heroGradient2)" strokeWidth="3" strokeLinecap="round" />
            <circle cx="140" cy="160" r="6" fill="url(#heroGradient2)" />
            <circle cx="140" cy="160" r="4" fill="white" opacity="0.6" />
          </g>

          {/* Right path - green */}
          <g>
            <line x1="280" y1="200" x2="260" y2="160" stroke="url(#heroGradient3)" strokeWidth="3" strokeLinecap="round" />
            <circle cx="260" cy="160" r="6" fill="url(#heroGradient3)" />
            <circle cx="260" cy="160" r="4" fill="white" opacity="0.6" />
          </g>

          {/* Center upward arrow representing progress */}
          <g>
            <line x1="200" y1="170" x2="200" y2="130" stroke="url(#heroGradient1)" strokeWidth="4" strokeLinecap="round" />
            {/* Arrow head */}
            <polygon points="200,125 195,135 205,135" fill="url(#heroGradient1)" />
          </g>
        </g>

        {/* Data nodes representing learning blocks */}
        <g className="hero-rotate" style={{ transformOrigin: '200px 120px' }}>
          {/* Node 1 - top right */}
          <g opacity="0.7">
            <circle cx="270" cy="60" r="5" fill="url(#heroGradient1)" />
            <line x1="250" y1="90" x2="270" y2="60" stroke="url(#heroGradient1)" strokeWidth="1.5" opacity="0.5" />
          </g>

          {/* Node 2 - right */}
          <g opacity="0.7">
            <circle cx="300" cy="120" r="5" fill="url(#heroGradient2)" />
            <line x1="245" y1="120" x2="300" y2="120" stroke="url(#heroGradient2)" strokeWidth="1.5" opacity="0.5" />
          </g>

          {/* Node 3 - bottom right */}
          <g opacity="0.7">
            <circle cx="270" cy="180" r="5" fill="url(#heroGradient3)" />
            <line x1="250" y1="150" x2="270" y2="180" stroke="url(#heroGradient3)" strokeWidth="1.5" opacity="0.5" />
          </g>

          {/* Node 4 - left */}
          <g opacity="0.7">
            <circle cx="100" cy="120" r="5" fill="url(#heroGradient1)" />
            <line x1="155" y1="120" x2="100" y2="120" stroke="url(#heroGradient1)" strokeWidth="1.5" opacity="0.5" />
          </g>
        </g>

        {/* Streak flame icon - bottom */}
        <g className="hero-float" style={{ animationDelay: '1s' }}>
          {/* Flame shape */}
          <path
            d="M 200 260 Q 190 245 190 230 Q 190 210 200 200 Q 210 210 210 230 Q 210 245 200 260 Z"
            fill="url(#heroGradient1)"
            filter="url(#heroShadow)"
          />
          {/* Inner flame */}
          <path
            d="M 200 250 Q 195 242 195 232 Q 195 220 200 215 Q 205 220 205 232 Q 205 242 200 250 Z"
            fill="white"
            opacity="0.6"
          />
          {/* Spark dots */}
          <circle cx="190" cy="220" r="2" fill="white" opacity="0.8" />
          <circle cx="210" cy="225" r="2" fill="white" opacity="0.8" />
        </g>

        {/* Streak counter representation */}
        <g opacity="0.8">
          <text
            x="200"
            y="285"
            textAnchor="middle"
            fontSize="12"
            fontWeight="bold"
            fill={isDark ? '#EF4444' : '#DC2626'}
          >
            28 Day Average Streak
          </text>
        </g>
      </svg>

      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating particles */}
        <div className="absolute top-10 left-1/4 w-2 h-2 bg-red-500 rounded-full opacity-60 animate-pulse" />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-blue-500 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-green-500 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '2s' }} />
      </div>
    </div>
  );
};
